import { useEffect, useState } from 'react';
import { subscribe } from './fieldQueue.js';
import { getLastSync, isSyncing, onSync } from './syncEngine.js';
import { attentionOf, pendingOf, summarize } from './queueCore.js';

/** Online / offline as the browser reports it, kept current. */
export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/**
 * The shared offline queue for a screen: every op, the pending ones (oldest first), the ones needing attention
 * (conflicts / not accepted), counts, sync state and the last sync time.
 */
export function useFieldSync() {
  const [ops, setOps] = useState([]);
  const [syncing, setSyncing] = useState(isSyncing());
  const [lastSync, setLastSync] = useState(null);
  const online = useOnline();
  useEffect(() => subscribe(setOps), []);
  useEffect(() => {
    let live = true;
    getLastSync().then((v) => live && setLastSync(v || null));
    const off = onSync((e) => {
      setSyncing(e.kind === 'start');
      if (e.kind === 'done') getLastSync().then((v) => live && setLastSync(v || null));
    });
    return () => {
      live = false;
      off();
    };
  }, []);
  return { ops, pending: pendingOf(ops), attention: attentionOf(ops), counts: summarize(ops), syncing, lastSync, online };
}
